// Ficha local — o que a família copia do prontuário do hospital.
//
// Muda poucas vezes por semana, mas sem isso nenhuma correlação faz sentido:
// PCR subindo com antibiótico trocado ontem é uma leitura; com antibiótico de
// dez dias, é outra.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  MARCADORES, ROTULO_GRUPO, acharMarcador, compararComReferencia, formatarValor, lerResultadoDeCultura, lerValorDeExame, tendencia, verbeteDoDispositivo, verbeteDoProcedimento, type Marcador, hojeISO, formatarData, doCampoParaISO, agoraParaCampo,
} from '@medicov1/shared';
import { chamar } from '../api';
import { Ditar } from '../componentes/Ditar';
import { Explicar, type Explicacao } from '../componentes/Explicar';
import './ficha.css';

type Aba = 'medicacoes' | 'dispositivos' | 'culturas' | 'exames' | 'laboratorio';

interface Medicacao {
  id: string; nome: string; dose: string | null; via: string | null;
  observacao: string | null; inicioEm: string; fimEm: string | null; emUso: boolean;
}
interface Dispositivo {
  id: string; nome: string; local: string | null; observacao: string | null;
  inseridoEm: string; retiradoEm: string | null; instalado: boolean; dias: number;
}
interface Cultura {
  id: string; material: string; coletadoEm: string;
  resultado: string | null; sensibilidade: string | null; aguardando: boolean;
}
interface Exame {
  id: string; marcador: string; valor: number; unidade: string; coletadoEm: string;
}
interface Procedimento {
  id: string; nome: string; situacao: 'solicitado' | 'agendado' | 'realizado' | 'cancelado';
  solicitadoEm: string; realizadoEm: string | null;
  resultado: string | null; observacao: string | null;
  pendente: boolean; diasDesdeOPedido: number;
}

const ROTULO_SITUACAO: Record<Procedimento['situacao'], string> = {
  solicitado: 'solicitado', agendado: 'agendado',
  realizado: 'realizado', cancelado: 'não vai ser feito',
};

// Data e hora vêm do módulo compartilhado, fixado em Brasília: o formulário
// abre com o dia daqui mesmo que o aparelho esteja com outro fuso.
const hoje = () => hojeISO();
const dataBr = formatarData;

export function Ficha({ pacienteId, paciente, aoVoltar }: {
  pacienteId: string;
  /** Como chamar a paciente nos textos: "Dona Maria". Vem do banco, não do código. */
  paciente: string;
  aoVoltar: () => void;
}) {
  // O que significa o item que a pessoa tocou. Null = ninguém tocou em nada.
  const [explicando, setExplicando] = useState<Explicacao | null>(null);
  const [aba, setAba] = useState<Aba>('medicacoes');
  const [medicacoes, setMedicacoes] = useState<Medicacao[]>([]);
  const [dispositivos, setDispositivos] = useState<Dispositivo[]>([]);
  const [culturas, setCulturas] = useState<Cultura[]>([]);
  const [exames, setExames] = useState<Exame[]>([]);
  const [procedimentos, setProcedimentos] = useState<Procedimento[]>([]);
  const [erro, setErro] = useState('');

  /**
   * Carrega as cinco listas. Cada uma por si: com `Promise.all` e um `catch` só,
   * uma falha momentânea de rede esvaziava a ficha INTEIRA e não tentava de
   * novo — e ficha vazia se parece com "não há nada registrado", que é a
   * mentira mais cara que esta tela poderia contar.
   */
  const carregar = useCallback(async () => {
    const resultados = await Promise.allSettled([
      chamar<Medicacao[]>(`/medicacoes?pacienteId=${pacienteId}`),
      chamar<Dispositivo[]>(`/dispositivos?pacienteId=${pacienteId}`),
      chamar<Cultura[]>(`/culturas?pacienteId=${pacienteId}`),
      chamar<Exame[]>(`/exames?pacienteId=${pacienteId}`),
      chamar<Procedimento[]>(`/procedimentos?pacienteId=${pacienteId}`),
    ]);

    const [m, d, c, e, pr] = resultados;
    // Só sobrescreve o que chegou: o que falhou mantém o que já estava na tela.
    if (m.status === 'fulfilled') setMedicacoes(m.value);
    if (d.status === 'fulfilled') setDispositivos(d.value);
    if (c.status === 'fulfilled') setCulturas(c.value);
    if (e.status === 'fulfilled') setExames(e.value);
    if (pr.status === 'fulfilled') setProcedimentos(pr.value);

    const falhou = resultados.filter((r) => r.status === 'rejected').length;
    setErro(
      falhou === 0
        ? ''
        : `Não consegui carregar ${falhou} de 5 partes da ficha. O que aparece abaixo pode estar incompleto.`,
    );
  }, [pacienteId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const abas: { chave: Aba; rotulo: string; conta: number }[] = [
    { chave: 'medicacoes', rotulo: 'Medicações', conta: medicacoes.filter((m) => m.emUso).length },
    { chave: 'dispositivos', rotulo: 'Dispositivos', conta: dispositivos.filter((d) => d.instalado).length },
    { chave: 'culturas', rotulo: 'Culturas', conta: culturas.filter((c) => c.aguardando).length },
    { chave: 'exames', rotulo: 'Imagem e exames', conta: procedimentos.filter((p) => p.pendente).length },
    { chave: 'laboratorio', rotulo: 'Laboratório', conta: new Set(exames.map((e) => e.marcador)).size },
  ];

  return (
    <main className="ficha">
      <header className="ficha__topo">
        <div>
          <h1>Ficha local</h1>
          <p className="ficha__nota">
            Registro paralelo da família — não é o prontuário do hospital.
          </p>
        </div>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      {erro && (
        <p className="aviso-erro ficha__erro">
          {erro}
          <button className="botao botao--fantasma" onClick={() => void carregar()}>
            Tentar de novo
          </button>
        </p>
      )}

      <nav className="ficha__abas">
        {abas.map((a) => (
          <button
            key={a.chave}
            className={`ficha__aba${aba === a.chave ? ' ficha__aba--on' : ''}`}
            onClick={() => setAba(a.chave)}
          >
            {a.rotulo}
            {a.conta > 0 && <b>{a.conta}</b>}
          </button>
        ))}
      </nav>

      {aba === 'medicacoes' && (
        <Medicacoes pacienteId={pacienteId} itens={medicacoes} aoMudar={carregar} />
      )}
      {aba === 'dispositivos' && (
        <Dispositivos pacienteId={pacienteId} itens={dispositivos} aoMudar={carregar}
          aoExplicar={setExplicando} />
      )}
      {aba === 'culturas' && (
        <Culturas pacienteId={pacienteId} itens={culturas} aoMudar={carregar}
          paciente={paciente} aoExplicar={setExplicando} />
      )}
      {aba === 'exames' && (
        <Procedimentos pacienteId={pacienteId} itens={procedimentos} aoMudar={carregar}
          aoExplicar={setExplicando} />
      )}
      {aba === 'laboratorio' && (
        <Laboratorio pacienteId={pacienteId} itens={exames} aoMudar={carregar}
          paciente={paciente} aoExplicar={setExplicando} />
      )}
      <Explicar explicacao={explicando} aoFechar={() => setExplicando(null)} />
    </main>
  );
}

// ------------------------------------------------------------- medicações

function Medicacoes({ pacienteId, itens, aoMudar }: {
  pacienteId: string; itens: Medicacao[]; aoMudar: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState({ nome: '', dose: '', via: '', inicioEm: hoje() });
  const emUso = itens.filter((m) => m.emUso);
  const encerradas = itens.filter((m) => !m.emUso);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    await chamar('/medicacoes', {
      metodo: 'POST',
      corpo: {
        pacienteId, nome: form.nome, inicioEm: form.inicioEm,
        ...(form.dose ? { dose: form.dose } : {}),
        ...(form.via ? { via: form.via } : {}),
      },
    });
    setForm({ nome: '', dose: '', via: '', inicioEm: hoje() });
    setAberto(false);
    aoMudar();
  }

  const encerrar = async (id: string) => {
    await chamar(`/medicacoes/${id}`, { metodo: 'PATCH', corpo: { fimEm: hoje() } });
    aoMudar();
  };

  return (
    <section className="secao">
      <div className="secao__cabeca">
        <h2>Em uso ({emUso.length})</h2>
        <button className="botao" onClick={() => setAberto(!aberto)}>
          {aberto ? 'Cancelar' : 'Adicionar'}
        </button>
      </div>

      {aberto && (
        <form className="formInline" onSubmit={enviar}>
          <div className="campo"><label>Medicação</label>
            <div className="comDitado">
              <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Daptomicina" />
              <Ditar valor={form.nome} aoMudar={(t) => setForm({ ...form, nome: t })}
                rotulo="ditar a medicação" />
            </div></div>
          <div className="campo"><label>Dose</label>
            <input value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })}
              placeholder="500 mg" /></div>
          <div className="campo"><label>Via</label>
            <input value={form.via} onChange={(e) => setForm({ ...form, via: e.target.value })}
              placeholder="endovenosa" /></div>
          <div className="campo"><label>Início</label>
            <input type="date" required value={form.inicioEm}
              onChange={(e) => setForm({ ...form, inicioEm: e.target.value })} /></div>
          <button className="botao" type="submit">Gravar</button>
        </form>
      )}

      {emUso.length === 0 && <p className="vazio">Nenhuma medicação registrada.</p>}
      <ul className="cartoes">
        {emUso.map((m) => (
          <li className="cartao cartao--ativo" key={m.id}>
            <div>
              <b>{m.nome}</b>
              <small>
                {[m.dose, m.via].filter(Boolean).join(' · ')}
                {m.dose || m.via ? ' · ' : ''}desde {dataBr(m.inicioEm)}
              </small>
            </div>
            <button className="botao botao--fantasma" onClick={() => encerrar(m.id)}>Encerrar</button>
          </li>
        ))}
      </ul>

      {encerradas.length > 0 && (
        <>
          <h2 className="secao__sub">Encerradas</h2>
          <ul className="cartoes">
            {encerradas.map((m) => (
              <li className="cartao cartao--fim" key={m.id}>
                <div>
                  <b>{m.nome}</b>
                  <small>{dataBr(m.inicioEm)} → {m.fimEm ? dataBr(m.fimEm) : '—'}</small>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ----------------------------------------------------------- dispositivos

function Dispositivos({ pacienteId, itens, aoMudar, aoExplicar }: {
  pacienteId: string; itens: Dispositivo[]; aoMudar: () => void;
  aoExplicar: (e: Explicacao) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState({ nome: '', local: '', inseridoEm: hoje() });
  const instalados = itens.filter((d) => d.instalado);
  const retirados = itens.filter((d) => !d.instalado);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    await chamar('/dispositivos', {
      metodo: 'POST',
      corpo: {
        pacienteId, nome: form.nome, inseridoEm: form.inseridoEm,
        ...(form.local ? { local: form.local } : {}),
      },
    });
    setForm({ nome: '', local: '', inseridoEm: hoje() });
    setAberto(false);
    aoMudar();
  }

  const retirar = async (id: string) => {
    await chamar(`/dispositivos/${id}`, { metodo: 'PATCH', corpo: { retiradoEm: hoje() } });
    aoMudar();
  };

  return (
    <section className="secao">
      <div className="secao__cabeca">
        <h2>Instalados ({instalados.length})</h2>
        <button className="botao" onClick={() => setAberto(!aberto)}>
          {aberto ? 'Cancelar' : 'Adicionar'}
        </button>
      </div>

      {aberto && (
        <form className="formInline" onSubmit={enviar}>
          <div className="campo"><label>Dispositivo</label>
            <div className="comDitado">
              <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Cateter venoso central" />
              <Ditar valor={form.nome} aoMudar={(t) => setForm({ ...form, nome: t })}
                rotulo="ditar o dispositivo" />
            </div></div>
          <div className="campo"><label>Local</label>
            <div className="comDitado">
              <input value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })}
                placeholder="veia femoral esquerda" />
              <Ditar valor={form.local} aoMudar={(t) => setForm({ ...form, local: t })}
                rotulo="ditar o local" />
            </div></div>
          <div className="campo"><label>Inserido em</label>
            <input type="date" required value={form.inseridoEm}
              onChange={(e) => setForm({ ...form, inseridoEm: e.target.value })} /></div>
          <button className="botao" type="submit">Gravar</button>
        </form>
      )}

      {instalados.length === 0 && <p className="vazio">Nenhum dispositivo registrado.</p>}
      <ul className="cartoes">
        {instalados.map((d) => (
          <li className="cartao cartao--ativo temExplicacao" key={d.id}
            onClick={() => {
              const v = verbeteDoDispositivo(d.nome);
              if (!v) return;
              aoExplicar({
                titulo: d.nome,
                situacao: `há ${d.dias} ${d.dias === 1 ? 'dia' : 'dias'}`,
                texto: [v.oQueE, v.porQueImporta].filter(Boolean).join(' '),
                pergunta: v.pergunta, peso: d.dias >= 14 ? 'atencao' : 'neutro',
              });
            }}>
            <div>
              <b>{d.nome}</b>
              <small>
                {d.local ? `${d.local} · ` : ''}desde {dataBr(d.inseridoEm)}
                {' · '}
                {/* Tempo de permanência importa: cada dia conta para infecção. */}
                <span className={d.dias >= 14 ? 'alerta' : ''}>{d.dias} {d.dias === 1 ? 'dia' : 'dias'}</span>
              </small>
            </div>
            <button className="botao botao--fantasma"
              onClick={(ev) => { ev.stopPropagation(); void retirar(d.id); }}>Retirar</button>
          </li>
        ))}
      </ul>

      {retirados.length > 0 && (
        <>
          <h2 className="secao__sub">Retirados</h2>
          <ul className="cartoes">
            {retirados.map((d) => (
              <li className="cartao cartao--fim" key={d.id}>
                <div>
                  <b>{d.nome}</b>
                  <small>{dataBr(d.inseridoEm)} → {d.retiradoEm ? dataBr(d.retiradoEm) : '—'} · {d.dias} dias</small>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// --------------------------------------------------------------- culturas

function Culturas({ pacienteId, itens, aoMudar, paciente, aoExplicar }: {
  pacienteId: string; itens: Cultura[]; aoMudar: () => void;
  paciente: string; aoExplicar: (e: Explicacao) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState({ material: '', coletadoEm: hoje(), resultado: '' });
  const [liberando, setLiberando] = useState<{ id: string; texto: string } | null>(null);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    await chamar('/culturas', {
      metodo: 'POST',
      corpo: {
        pacienteId, material: form.material, coletadoEm: form.coletadoEm,
        ...(form.resultado ? { resultado: form.resultado } : {}),
      },
    });
    setForm({ material: '', coletadoEm: hoje(), resultado: '' });
    setAberto(false);
    aoMudar();
  }

  async function liberar(evento: FormEvent) {
    evento.preventDefault();
    if (!liberando) return;
    await chamar(`/culturas/${liberando.id}`, { metodo: 'PATCH', corpo: { resultado: liberando.texto } });
    setLiberando(null);
    aoMudar();
  }

  return (
    <section className="secao">
      <div className="secao__cabeca">
        <h2>Culturas e antibiograma</h2>
        <button className="botao" onClick={() => setAberto(!aberto)}>
          {aberto ? 'Cancelar' : 'Adicionar'}
        </button>
      </div>

      {aberto && (
        <form className="formInline" onSubmit={enviar}>
          <div className="campo"><label>Material</label>
            <div className="comDitado">
              <input required value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })}
                placeholder="Hemocultura" />
              <Ditar valor={form.material} aoMudar={(t) => setForm({ ...form, material: t })}
                rotulo="ditar o material" />
            </div></div>
          <div className="campo"><label>Coletado em</label>
            <input type="date" required value={form.coletadoEm}
              onChange={(e) => setForm({ ...form, coletadoEm: e.target.value })} /></div>
          <div className="campo campo--largo"><label>Resultado (deixe vazio se ainda não saiu)</label>
            <div className="comDitado">
              <input value={form.resultado} onChange={(e) => setForm({ ...form, resultado: e.target.value })}
                placeholder="negativa / Klebsiella > 100.000" />
              <Ditar valor={form.resultado} aoMudar={(t) => setForm({ ...form, resultado: t })}
                rotulo="ditar o resultado" />
            </div></div>
          <button className="botao" type="submit">Gravar</button>
        </form>
      )}

      {itens.length === 0 && <p className="vazio">Nenhuma cultura registrada.</p>}
      <ul className="cartoes">
        {itens.map((c) => (
          <li className={`cartao temExplicacao ${c.aguardando ? 'cartao--espera' : 'cartao--ativo'}`} key={c.id}
            onClick={() => {
              // A leitura do resultado é feita aqui, no navegador, por regra
              // escrita à mão — não por IA e sem mandar nada para fora.
              const leitura = lerResultadoDeCultura(c.material, c.resultado, paciente);
              aoExplicar({
                titulo: `${c.material} de ${dataBr(c.coletadoEm)}`,
                situacao: leitura.situacao, texto: leitura.texto,
                pergunta: leitura.pergunta, peso: leitura.peso,
              });
            }}>
            <div>
              <b>{c.material}</b>
              <small>
                coletada em {dataBr(c.coletadoEm)} ·{' '}
                {c.aguardando
                  ? <span className="espera">aguardando resultado</span>
                  : c.resultado}
              </small>
              {c.sensibilidade && <small>sensível a: {c.sensibilidade}</small>}
            </div>
            {c.aguardando && (
              liberando?.id === c.id ? (
                <form className="liberar" onSubmit={liberar} onClick={(ev) => ev.stopPropagation()}>
                  <input autoFocus value={liberando.texto} placeholder="resultado"
                    onChange={(e) => setLiberando({ id: c.id, texto: e.target.value })} />
                  <Ditar valor={liberando.texto} aoMudar={(t) => setLiberando({ id: c.id, texto: t })}
                    rotulo="ditar o resultado" />
                  <button className="botao" type="submit">Salvar</button>
                </form>
              ) : (
                <button className="botao botao--fantasma"
                  onClick={(ev) => { ev.stopPropagation(); setLiberando({ id: c.id, texto: '' }); }}>
                  Lançar resultado
                </button>
              )
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}


// ------------------------------------------------- imagem e outros exames

/**
 * Tomografia, ressonância, eletroencefalograma, endoscopia.
 *
 * Separado do laboratório porque aqui o resultado é laudo em texto, e o que
 * mais importa costuma ser o estado: "ressonância pedida há 5 dias e ainda não
 * fez" é a pergunta pronta para a próxima visita da equipe.
 */
function Procedimentos({ pacienteId, itens, aoMudar, aoExplicar }: {
  pacienteId: string; itens: Procedimento[]; aoMudar: () => void;
  aoExplicar: (e: Explicacao) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState({ nome: '', solicitadoEm: hoje(), observacao: '' });
  const [lancando, setLancando] = useState<{ id: string; texto: string } | null>(null);

  const pendentes = itens.filter((p) => p.pendente);
  const fechados = itens.filter((p) => !p.pendente);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    await chamar('/procedimentos', {
      metodo: 'POST',
      corpo: {
        pacienteId, nome: form.nome, solicitadoEm: form.solicitadoEm,
        ...(form.observacao ? { observacao: form.observacao } : {}),
      },
    });
    setForm({ nome: '', solicitadoEm: hoje(), observacao: '' });
    setAberto(false);
    aoMudar();
  }

  async function lancarResultado(evento: FormEvent) {
    evento.preventDefault();
    if (!lancando) return;
    await chamar(`/procedimentos/${lancando.id}`, {
      metodo: 'PATCH',
      corpo: { situacao: 'realizado', realizadoEm: hoje(), resultado: lancando.texto },
    });
    setLancando(null);
    aoMudar();
  }

  const cancelar = async (id: string) => {
    await chamar(`/procedimentos/${id}`, { metodo: 'PATCH', corpo: { situacao: 'cancelado' } });
    aoMudar();
  };

  const cartao = (p: Procedimento) => (
    <li className={`cartao temExplicacao cartao--${p.pendente ? 'espera' : p.situacao === 'cancelado' ? 'fim' : 'ativo'}`} key={p.id}
      onClick={() => {
        const v = verbeteDoProcedimento(p.nome);
        if (!v) return;
        aoExplicar({
          titulo: p.nome,
          situacao: ROTULO_SITUACAO[p.situacao],
          texto: [v.oQueE, v.porQueImporta].filter(Boolean).join(' '),
          pergunta: v.pergunta, peso: p.pendente ? 'aguardando' : 'neutro',
        });
      }}>
      <div>
        <b>{p.nome}</b>
        <small>
          pedido em {dataBr(p.solicitadoEm)}
          {p.realizadoEm && ` · feito em ${dataBr(p.realizadoEm)}`}
          {' · '}
          <span className={p.pendente && p.diasDesdeOPedido >= 3 ? 'alerta' : ''}>
            {ROTULO_SITUACAO[p.situacao]}
            {p.pendente && ` há ${p.diasDesdeOPedido} ${p.diasDesdeOPedido === 1 ? 'dia' : 'dias'}`}
          </span>
        </small>
        {p.resultado && <small className="cartao__laudo">{p.resultado}</small>}
        {p.observacao && <small className="cartao__obs">{p.observacao}</small>}
      </div>

      {p.pendente && (
        lancando?.id === p.id ? (
          <form className="liberar" onSubmit={lancarResultado} onClick={(ev) => ev.stopPropagation()}>
            <input autoFocus value={lancando.texto} placeholder="o que deu"
              onChange={(e) => setLancando({ id: p.id, texto: e.target.value })} />
            <Ditar valor={lancando.texto} aoMudar={(t) => setLancando({ id: p.id, texto: t })}
              rotulo="ditar o resultado" />
            <button className="botao" type="submit">Salvar</button>
          </form>
        ) : (
          <div className="cartao__acoes">
            <button className="botao botao--fantasma"
              onClick={(ev) => { ev.stopPropagation(); setLancando({ id: p.id, texto: '' }); }}>
              Lançar resultado
            </button>
            {/* Cancelado não apaga a linha: pedido barrado por contraindicação
                é informação, e some da tela seria perdê-la. */}
            <button className="botao botao--fantasma"
              onClick={(ev) => { ev.stopPropagation(); void cancelar(p.id); }}>
              Não vai ser feito
            </button>
          </div>
        )
      )}
    </li>
  );

  return (
    <section className="secao">
      <div className="secao__cabeca">
        <h2>Aguardando ({pendentes.length})</h2>
        <button className="botao" onClick={() => setAberto(!aberto)}>
          {aberto ? 'Cancelar' : 'Adicionar'}
        </button>
      </div>

      {aberto && (
        <form className="formInline" onSubmit={enviar}>
          <div className="campo"><label>Exame</label>
            <div className="comDitado">
              <input required value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Tomografia de crânio" />
              <Ditar valor={form.nome} aoMudar={(t) => setForm({ ...form, nome: t })}
                rotulo="ditar o exame" />
            </div>
          </div>
          <div className="campo"><label>Pedido em</label>
            <input type="date" required value={form.solicitadoEm}
              onChange={(e) => setForm({ ...form, solicitadoEm: e.target.value })} /></div>
          <div className="campo campo--largo"><label>Observação</label>
            <div className="comDitado">
              <input value={form.observacao}
                onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                placeholder="verificar se pode com o marca-passo" />
              <Ditar valor={form.observacao} aoMudar={(t) => setForm({ ...form, observacao: t })}
                rotulo="ditar a observação" />
            </div>
          </div>
          <button className="botao" type="submit">Gravar</button>
        </form>
      )}

      {pendentes.length === 0 && <p className="vazio">Nenhum exame aguardando.</p>}
      <ul className="cartoes">{pendentes.map(cartao)}</ul>

      {fechados.length > 0 && (
        <>
          <h2 className="secao__sub">Já resolvidos</h2>
          <ul className="cartoes">{fechados.map(cartao)}</ul>
        </>
      )}
    </section>
  );
}

// ------------------------------------------------------------ laboratório

function Laboratorio({ pacienteId, itens, aoMudar, paciente, aoExplicar }: {
  pacienteId: string; itens: Exame[]; aoMudar: () => void;
  paciente: string; aoExplicar: (e: Explicacao) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [coletadoEm, setColetadoEm] = useState(() => agoraParaCampo());
  const [valores, setValores] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);

  // Uma série por marcador, do mais recente para o mais antigo.
  const porMarcador = new Map<string, Exame[]>();
  for (const e of itens) porMarcador.set(e.marcador, [...(porMarcador.get(e.marcador) ?? []), e]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setSalvando(true);
    try {
      const quando = doCampoParaISO(coletadoEm);
      for (const [marcador, bruto] of Object.entries(valores)) {
        const texto = bruto.replace(',', '.').trim();
        if (!texto) continue;
        const valor = Number(texto);
        if (!Number.isFinite(valor)) continue;
        await chamar('/exames', { metodo: 'POST', corpo: { pacienteId, marcador, valor, coletadoEm: quando } });
      }
      setValores({});
      setAberto(false);
      aoMudar();
    } finally {
      setSalvando(false);
    }
  }

  const porGrupo = new Map<Marcador['grupo'], Marcador[]>();
  for (const m of MARCADORES) porGrupo.set(m.grupo, [...(porGrupo.get(m.grupo) ?? []), m]);

  return (
    <section className="secao">
      <div className="secao__cabeca">
        <h2>Laboratório</h2>
        <button className="botao" onClick={() => setAberto(!aberto)}>
          {aberto ? 'Cancelar' : 'Lançar exames'}
        </button>
      </div>

      {aberto && (
        <form className="lab__form" onSubmit={enviar}>
          <div className="campo" style={{ maxWidth: 280 }}>
            <label>Data e hora da coleta</label>
            <input type="datetime-local" required value={coletadoEm}
              onChange={(e) => setColetadoEm(e.target.value)} />
          </div>
          <p className="lab__dica">Preencha só o que saiu no exame — campo vazio é ignorado.</p>

          {[...porGrupo.entries()].map(([grupo, marcadores]) => (
            <fieldset className="lab__grupo" key={grupo}>
              <legend>{ROTULO_GRUPO[grupo]}</legend>
              <div className="lab__campos">
                {marcadores.map((m) => (
                  <div className="campo" key={m.chave}>
                    <label htmlFor={m.chave}>{m.rotulo} <span>{m.unidade}</span></label>
                    <input id={m.chave} inputMode="decimal" value={valores[m.chave] ?? ''}
                      onChange={(e) => setValores({ ...valores, [m.chave]: e.target.value })} />
                  </div>
                ))}
              </div>
            </fieldset>
          ))}

          <button className="botao" type="submit" disabled={salvando}>
            {salvando ? 'Gravando…' : 'Gravar exames'}
          </button>
        </form>
      )}

      {porMarcador.size === 0 && <p className="vazio">Nenhum exame lançado.</p>}

      {porMarcador.size > 0 && (
        <div className="tabbox">
          <table className="lab__tabela">
            <thead>
              <tr>
                {/* A faixa fica na tabela, não só na explicação: é o que o
                    laudo do laboratório traz ao lado de cada número, e sem ela
                    "está alto" não diz alto em relação a quê. */}
                <th>Exame</th><th>Último</th><th>Referência</th><th>Anterior</th><th>Tendência</th><th>Coletado</th>
              </tr>
            </thead>
            <tbody>
              {[...porMarcador.entries()].map(([chave, serie]) => {
                const marcador = acharMarcador(chave);
                const ultimo = serie[0]!;
                const anterior = serie[1];
                const situacao = compararComReferencia(chave, ultimo.valor);
                const direcao = anterior ? tendencia(anterior.valor, ultimo.valor, chave) : null;

                return (
                  // data-rotulo vira o rótulo da linha quando a tabela se
                  // empilha no celular (ver ficha.css).
                  <tr key={chave} className="temExplicacao"
                    onClick={() => {
                      const leitura = lerValorDeExame(chave, situacao, paciente, Number(ultimo.valor));
                      if (!leitura) return;
                      aoExplicar({
                        titulo: marcador?.rotulo ?? chave,
                        situacao: situacao === 'alto' ? 'acima da faixa usual'
                          : situacao === 'baixo' ? 'abaixo da faixa usual'
                          : situacao === 'dentro' ? 'dentro da faixa usual' : undefined,
                        texto: leitura.texto, pergunta: leitura.pergunta,
                        peso: situacao === 'dentro' ? 'tranquilizador'
                          : situacao === 'desconhecido' ? 'neutro' : 'atencao',
                      });
                    }}>
                    <td><b>{marcador?.rotulo ?? chave}</b></td>
                    <td className={`valor valor--${situacao}`} data-rotulo="Último">
                      {formatarValor(chave, ultimo.valor)} <small>{ultimo.unidade}</small>
                    </td>
                    <td className="referencia" data-rotulo="Referência">
                      {marcador?.referencia
                        ? `${formatarValor(chave, marcador.referencia.min)}–${formatarValor(chave, marcador.referencia.max)}`
                        : '—'}
                    </td>
                    <td className="anterior" data-rotulo="Anterior">
                      {anterior ? formatarValor(chave, anterior.valor) : '—'}
                    </td>
                    <td className={`direcao direcao--${direcao ?? 'nenhuma'}`} data-rotulo="Tendência">
                      {direcao === 'subiu' ? '↑ subiu' : direcao === 'caiu' ? '↓ caiu' : direcao ? 'estável' : '—'}
                    </td>
                    <td className="quando" data-rotulo="Coletado">
                      {/* INSTANTE, não dia puro: sem o `new Date`, exame colhido
                          às 22h aparece com a data do dia seguinte. */}
                      {formatarData(new Date(ultimo.coletadoEm))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {porMarcador.size > 0 && (
        <p className="lab__legenda">
          Cor indica valor fora da faixa usual de adulto — é sinalização, não interpretação.
          Quem interpreta é a equipe.
        </p>
      )}
    </section>
  );
}
